import { Badge, Button, Dialog, Textarea, type BadgeTone } from '@artosera/ui';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SIGNALEMENT_MAX,
  useGestesAccesVenio,
  useProcesVerbalReprise,
  useRecetteReprise,
  type LigneRepriseGalerie,
  type RecetteReprise,
} from '../../api/accesVenio.js';
import { ouvrirUrlSignee } from '../../api/client.js';
import { estApiError } from '../../api/errors.js';
import accesStyles from './AccesVenioPage.module.css';
import styles from './CarteRepriseDonnees.module.css';

const NOMBRE = new Intl.NumberFormat('fr-FR');

type Boite = 'validation' | 'signalement' | null;

/** Ce qui n'est pas entré dans la galerie, sans que ce soit une erreur : ignoré, refusé ou écarté. */
function nonReprises(ligne: LigneRepriseGalerie): number {
  return ligne.ignores + ligne.refuses + ligne.ecartes;
}

/**
 * La carte « Reprise de vos données » (spec § 8.11, A3) : les compteurs par
 * nature, où en est la reprise, et les deux gestes de la direction — valider,
 * ou signaler un problème. Elle ne montre rien tant que la galerie n'a aucun
 * dossier de reprise. Le ton est celui d'une galerie : ni lots, ni pivots,
 * seulement ce que Venio a repris et ce qu'il reste à vérifier.
 */
export function CarteRepriseDonnees({ galerieSlug }: { galerieSlug: string }): JSX.Element | null {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const recette = useRecetteReprise(galerieSlug);
  const gestes = useGestesAccesVenio(galerieSlug);
  const procesVerbal = useProcesVerbalReprise(galerieSlug);
  const [boite, setBoite] = useState<Boite>(null);
  const [texte, setTexte] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const cle = 'regie:reglages.accesVenio.reprise';

  const langue = i18n.resolvedLanguage === 'en' ? 'en-GB' : 'fr-FR';
  const jour = (iso: string): string =>
    new Intl.DateTimeFormat(langue, { dateStyle: 'long', timeZone: 'Europe/Paris' }).format(
      new Date(iso),
    );
  const date = (iso: string): string =>
    new Intl.DateTimeFormat(langue, {
      dateStyle: 'long',
      timeStyle: 'short',
      timeZone: 'Europe/Paris',
    }).format(new Date(iso));

  const echec = (erreur: Error): void =>
    setMessage(estApiError(erreur) ? erreur.message : t('commun:erreurReseau'));

  if (recette.isLoading) return null;
  if (recette.isError) {
    return (
      <section className={accesStyles.carte} aria-labelledby="acces-venio-reprise">
        <h2 id="acces-venio-reprise" className={accesStyles.titreSection}>
          {t(`${cle}.titre`)}
        </h2>
        <p className={accesStyles.alerte} role="alert">
          {estApiError(recette.error) ? recette.error.message : t('commun:erreurReseau')}
        </p>
      </section>
    );
  }
  const donnees: RecetteReprise | null = recette.data?.recette ?? null;
  if (donnees === null || donnees.etat === 'abandonne') return null;

  const { validation } = donnees;
  const statut = validation ? 'validee' : donnees.peutValider ? 'aValider' : 'attente';
  const tonStatut: Record<typeof statut, BadgeTone> = {
    validee: 'available',
    aValider: 'reserved',
    attente: 'neutral',
  };
  const enCours = gestes.validerReprise.isPending || gestes.signalerReprise.isPending;
  // Une reprise validée n'a plus à être expliquée : les écarts ne se signalent qu'avant.
  const enEcart = validation ? [] : donnees.natures.filter((ligne) => ligne.ecart !== 0);
  const signalements = [...donnees.signalements].sort(
    (a, b) => new Date(b.le).getTime() - new Date(a.le).getTime(),
  );

  const fermer = (): void => {
    setBoite(null);
    setTexte('');
  };

  const valider = (): void => {
    setMessage(null);
    gestes.validerReprise.mutate(undefined, { onError: echec, onSuccess: fermer });
  };
  const signaler = (): void => {
    if (texte.trim() === '') return;
    setMessage(null);
    gestes.signalerReprise.mutate(texte.trim(), { onError: echec, onSuccess: fermer });
  };
  const ouvrirProcesVerbal = (): void => {
    setMessage(null);
    procesVerbal.mutate(undefined, {
      onError: echec,
      onSuccess: (reponse) => ouvrirUrlSignee(reponse.url),
    });
  };

  return (
    <section className={accesStyles.carte} aria-labelledby="acces-venio-reprise">
      <div className={styles.tete}>
        <h2 id="acces-venio-reprise" className={accesStyles.titreSection}>
          {t(`${cle}.titre`)}
        </h2>
        <Badge tone={tonStatut[statut]}>{t(`${cle}.etats.${statut}`)}</Badge>
      </div>
      <p className={accesStyles.description}>
        {t(
          `${cle}.${validation ? 'texteValidee' : donnees.peutValider ? 'texteAValider' : 'texteAttente'}`,
        )}
      </p>
      {message ? (
        <p className={accesStyles.alerte} role="alert">
          {message}
        </p>
      ) : null}

      {donnees.natures.length > 0 ? (
        <ul className={styles.natures} aria-label={t(`${cle}.natures`)}>
          {donnees.natures.map((ligne) => (
            <li key={ligne.nature} className={styles.nature} data-ecart={enEcart.includes(ligne)}>
              <h3 className={styles.nom}>
                {t(`regie:reglages.accesVenio.natures.${ligne.nature}`)}
              </h3>
              <dl className={styles.chiffres}>
                <div>
                  <dt>{t(`${cle}.colonnes.total`)}</dt>
                  <dd>{NOMBRE.format(ligne.total)}</dd>
                </div>
                <div>
                  <dt>{t(`${cle}.colonnes.crees`)}</dt>
                  <dd>{NOMBRE.format(ligne.crees)}</dd>
                </div>
                <div>
                  <dt>{t(`${cle}.colonnes.completes`)}</dt>
                  <dd>{NOMBRE.format(ligne.completes)}</dd>
                </div>
                <div>
                  <dt>{t(`${cle}.colonnes.nonReprises`)}</dt>
                  <dd>{NOMBRE.format(nonReprises(ligne))}</dd>
                </div>
                <div className={enEcart.includes(ligne) ? styles.ecart : undefined}>
                  <dt>{t(`${cle}.colonnes.ecart`)}</dt>
                  <dd>{NOMBRE.format(ligne.ecart)}</dd>
                </div>
              </dl>
            </li>
          ))}
        </ul>
      ) : null}

      {enEcart.map((ligne) => (
        <p key={ligne.nature} className={styles.avertissement} role="note">
          {t(`${cle}.ecart`, {
            count: Math.abs(ligne.ecart),
            nature: t(`regie:reglages.accesVenio.natures.${ligne.nature}`),
          })}
        </p>
      ))}
      {donnees.aRevoir > 0 ? (
        <p className={accesStyles.discret}>{t(`${cle}.aRevoir`, { count: donnees.aRevoir })}</p>
      ) : null}

      <section aria-labelledby="reprise-controles">
        <h3 id="reprise-controles" className={accesStyles.titreBloc}>
          {t(`${cle}.controles`)}
        </h3>
        <ul className={styles.controles}>
          {donnees.controles.map((controle) => (
            <li key={controle.cle} data-fait={controle.coche}>
              <span>{t(`${cle}.controle.${controle.cle}`)}</span>
              <span className={accesStyles.discret}>
                {controle.coche && controle.le
                  ? t(`${cle}.controleFait`, { date: jour(controle.le) })
                  : t(`${cle}.controleAFaire`)}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {donnees.basculePrevueLe ? (
        <p className={accesStyles.discret}>
          {t(`${cle}.ouverturePrevue`, { date: jour(donnees.basculePrevueLe) })}
        </p>
      ) : null}

      {signalements.length > 0 ? (
        <section aria-labelledby="reprise-signalements">
          <h3 id="reprise-signalements" className={accesStyles.titreBloc}>
            {t(`${cle}.signalements`)}
          </h3>
          <ul className={styles.signalements}>
            {signalements.map((signalement) => (
              <li key={`${signalement.le}-${signalement.par}`}>
                <p>{signalement.texte}</p>
                <span className={accesStyles.discret}>{date(signalement.le)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {validation ? (
        <article className={accesStyles.actif} aria-labelledby="reprise-validee">
          <h3 id="reprise-validee" className={accesStyles.titreBloc}>
            {t(`${cle}.pv`)}
          </h3>
          <p className={styles.sansMarge}>
            {t(`${cle}.valideePar`, { par: validation.par, date: date(validation.le) })}{' '}
            {validation.pv.disponible
              ? t(`${cle}.${validation.pv.rangeAuCoffre ? 'pvCoffre' : 'pvDocuments'}`)
              : t(`${cle}.pvAttente`)}
          </p>
          {validation.pv.disponible ? (
            <div className={accesStyles.actions}>
              <Button
                variant="secondary"
                loading={procesVerbal.isPending}
                onClick={ouvrirProcesVerbal}
              >
                {t(`${cle}.pvTelecharger`)}
              </Button>
            </div>
          ) : null}
        </article>
      ) : donnees.peutValider ? (
        <div className={accesStyles.actions}>
          <Button disabled={enCours} onClick={() => setBoite('validation')}>
            {t(`${cle}.valider`)}
          </Button>
          <Button variant="secondary" disabled={enCours} onClick={() => setBoite('signalement')}>
            {t(`${cle}.signaler`)}
          </Button>
        </div>
      ) : null}

      <Dialog
        open={boite === 'validation'}
        onOpenChange={(ouvert) => {
          if (!ouvert) fermer();
        }}
        title={t(`${cle}.validation.titre`)}
        description={t(`${cle}.validation.texte`)}
        closeLabel={t('regie:reglages.accesVenio.annuler')}
        footer={
          <>
            <Button variant="secondary" onClick={fermer}>
              {t('regie:reglages.accesVenio.annuler')}
            </Button>
            <Button loading={gestes.validerReprise.isPending} onClick={valider}>
              {t(`${cle}.validation.action`)}
            </Button>
          </>
        }
      />

      <Dialog
        open={boite === 'signalement'}
        onOpenChange={(ouvert) => {
          if (!ouvert) fermer();
        }}
        title={t(`${cle}.signalement.titre`)}
        description={t(`${cle}.signalement.texte`)}
        closeLabel={t('regie:reglages.accesVenio.annuler')}
        footer={
          <>
            <Button variant="secondary" onClick={fermer}>
              {t('regie:reglages.accesVenio.annuler')}
            </Button>
            <Button
              loading={gestes.signalerReprise.isPending}
              disabled={texte.trim() === ''}
              onClick={signaler}
            >
              {t(`${cle}.signalement.action`)}
            </Button>
          </>
        }
      >
        <div className={styles.champ}>
          <label htmlFor="reprise-signalement-texte">{t(`${cle}.signalement.champ`)}</label>
          <Textarea
            id="reprise-signalement-texte"
            rows={5}
            maxLength={SIGNALEMENT_MAX}
            value={texte}
            onChange={(evenement) => setTexte(evenement.target.value)}
          />
          <span className={accesStyles.discret}>
            {t(`${cle}.signalement.restant`, { count: SIGNALEMENT_MAX })}
          </span>
        </div>
      </Dialog>
    </section>
  );
}
